import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from "react-router-dom";
import data from '../../data/data.json';
import { useLanguage } from '../../contexts/LanguageContext';
import useDocumentTitle from '../../hooks/useDocumentTitle';

// Pages are fixed artwork, so they are rendered to images once by
// tools/render_story_pages.py and served as plain <img>. This replaces
// react-pdf and react-flip-page: a 1 MB pdf.js worker, a canvas per page, and
// an unmaintained flipbook that accumulated DOM as you read and left blank
// pages behind it. The PDFs stay exactly where they are, for download and
// printing.
//
// Only the page being read and its immediate neighbours are in the document,
// so memory stays flat however long the story is.
const NEIGHBOURS = 1;

// The PDFs' own page proportions.
const PAGE_RATIO = 612 / 437;

// Long enough to read as a sheet of paper moving, short enough not to be in
// the way when a child is turning pages quickly.
const TURN_MS = 520;

const StoryFlipBook = () => {
    const { storyId } = useParams();
    const { translate, lang } = useLanguage();
    useDocumentTitle(`${translate('storyBook')} ${Number(storyId) + 1}`);

    const story = data.stories[storyId];
    const entry = story?.lang?.[lang];
    const pageCount = entry?.pages || 0;
    const pageDir = entry?.pageDir;
    const pdfUrl = entry?.pdf || null;

    const [current, setCurrent] = useState(0);
    const [turning, setTurning] = useState(null); // { dir, from, to } | null
    const [size, setSize] = useState({ width: 480, height: 672 });
    const [loaded, setLoaded] = useState(() => new Set());

    const wrapRef = useRef(null);
    const touchRef = useRef(null);

    useEffect(() => {
        setCurrent(0);
        setLoaded(new Set());
    }, [storyId, lang]);

    // Fit the book to whatever room there is, keeping the page's own shape.
    useEffect(() => {
        if (!wrapRef.current) return;

        const observer = new ResizeObserver(([entry]) => {
            const available = entry.contentRect.width;
            const maxHeight = window.innerHeight - 190;
            let height = maxHeight;
            let width = height / PAGE_RATIO;
            if (width > available) {
                width = available;
                height = width * PAGE_RATIO;
            }
            setSize({ width: Math.round(width), height: Math.round(height) });
        });

        observer.observe(wrapRef.current);
        return () => observer.disconnect();
    }, []);

    // Going forward, the page you were on lifts and swings over to the left on
    // its spine, uncovering the next one. Going back, the previous page swings
    // down into place again. Either way one sheet is moving and the other is
    // still, which is what turning a page actually looks like.
    const go = useCallback((delta) => {
        setCurrent((page) => {
            const next = page + delta;
            if (next < 0 || next >= pageCount) return page;
            setTurning({ dir: delta > 0 ? 'next' : 'prev', from: page, to: next });
            window.setTimeout(() => setTurning(null), TURN_MS);
            return next;
        });
    }, [pageCount]);

    // Arrow keys read the book, which the old flipbook never supported.
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'ArrowRight') go(1);
            else if (e.key === 'ArrowLeft') go(-1);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [go]);

    const onTouchStart = (e) => {
        touchRef.current = e.touches[0].clientX;
    };

    const onTouchEnd = (e) => {
        if (touchRef.current === null) return;
        const dx = e.changedTouches[0].clientX - touchRef.current;
        touchRef.current = null;
        if (Math.abs(dx) > 45) go(dx < 0 ? 1 : -1);
    };

    const markLoaded = (i) => setLoaded((s) => (s.has(i) ? s : new Set(s).add(i)));

    if (!pageDir || pageCount === 0) {
        return (
            <div className="book-wrapper" ref={wrapRef}>
                <div className="book-cover">
                    <div className="book-content">
                        <div className="no-story-message">{translate('story_coming_soon')}</div>
                    </div>
                </div>
            </div>
        );
    }

    const visible = [];
    for (let i = current - NEIGHBOURS; i <= current + NEIGHBOURS; i++) {
        if (i >= 0 && i < pageCount) visible.push(i);
    }

    return (
        <div className="book-wrapper" ref={wrapRef}>
            <div className="book-cover">
                <div
                    className="book-content"
                    style={{ width: size.width, height: size.height }}
                    onTouchStart={onTouchStart}
                    onTouchEnd={onTouchEnd}
                >
                    {visible.map((i) => {
                        // The sheet that moves: on the way forward it is the
                        // page being left behind, on the way back it is the
                        // page coming home.
                        const moving = turning
                            && i === (turning.dir === 'next' ? turning.from : turning.to);
                        return (
                            <img
                                key={i}
                                src={`${pageDir}/${i}.webp`}
                                alt={`${translate('page_of')} ${i + 1}`}
                                className={
                                    'book-page-image'
                                    + (i === current ? ' is-current' : '')
                                    + (moving ? ` is-turning turn-${turning.dir}` : '')
                                }
                                draggable="false"
                                onLoad={() => markLoaded(i)}
                                // A cached image can finish before React attaches
                                // onLoad, which would leave the overlay up forever.
                                ref={(el) => { if (el && el.complete) markLoaded(i); }}
                                aria-hidden={i !== current}
                            />
                        );
                    })}
                    {turning && <div className={`book-turn-shadow turn-${turning.dir}`} aria-hidden="true" />}
                    {!loaded.has(current) && (
                        <div className="book-loading">{translate('loading_story')}</div>
                    )}
                </div>

                <div className="book-controls">
                    <span className="book-page-count" aria-live="polite">
                        {`${translate('page_of')} ${current + 1} ${translate('of')} ${pageCount}`}
                    </span>
                    {pdfUrl && (
                        <a
                            href={pdfUrl}
                            download={`story_${storyId}.pdf`}
                            className="button download-button"
                            aria-label={translate('download')}
                        >
                            <img src="/assets/img/icons/download-white.png" alt="" className="nav-icon" />
                        </a>
                    )}
                    <button
                        className="button"
                        onClick={() => go(-1)}
                        disabled={current === 0}
                        aria-label={translate('previous')}
                    >
                        <img src="/assets/img/icons/arrow-left.png" alt="" className="nav-icon" />
                    </button>
                    <button
                        className="button"
                        onClick={() => go(1)}
                        disabled={current >= pageCount - 1}
                        aria-label={translate('next')}
                    >
                        <img src="/assets/img/icons/arrow-right.png" alt="" className="nav-icon" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default StoryFlipBook;
